import _asyncToGenerator from "/builds/assist/zerobounce-website/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _regeneratorRuntime from "/builds/assist/zerobounce-website/node_modules/@babel/runtime/regenerator/index.js";
/*!
 * Copyright (c) 2015-present, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * 
 * See the License for the specific language governing permissions and limitations under the License.
 */

import { warn } from '../util/console.js';
import { removeNils } from '../util/object.js';
import '../errors/WWWAuthError.js';
import { createOAuthMeta } from '../oidc/util/oauthMeta.js';
import '../http/request.js';
import 'tiny-emitter';
import 'js-cookie';
import 'cross-fetch';
import '../oidc/types/Token.js';
import '../_virtual/_tslib.js';
function createTransactionMeta(_x) {
  return _createTransactionMeta.apply(this, arguments);
}
function _createTransactionMeta() {
  _createTransactionMeta = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee(authClient) {
    var options,
      tokenParams,
      pkceMeta,
      _Object$assign,
      _Object$assign$flow,
      flow,
      _Object$assign$withCr,
      withCredentials,
      _Object$assign$activa,
      activationToken,
      _Object$assign$recove,
      recoveryToken,
      _Object$assign$maxAge,
      maxAge,
      _Object$assign$acrVal,
      acrValues,
      meta,
      _args = arguments;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          options = _args.length > 1 && _args[1] !== undefined ? _args[1] : {};
          _context.next = 3;
          return authClient.token.prepareTokenParams(options);
        case 3:
          tokenParams = _context.sent;
          pkceMeta = createOAuthMeta(authClient, tokenParams);
          _Object$assign = Object.assign(Object.assign({}, authClient.options), options), _Object$assign$flow = _Object$assign.flow, flow = _Object$assign$flow === void 0 ? 'default' : _Object$assign$flow, _Object$assign$withCr = _Object$assign.withCredentials, withCredentials = _Object$assign$withCr === void 0 ? true : _Object$assign$withCr, _Object$assign$activa = _Object$assign.activationToken, activationToken = _Object$assign$activa === void 0 ? undefined : _Object$assign$activa, _Object$assign$recove = _Object$assign.recoveryToken, recoveryToken = _Object$assign$recove === void 0 ? undefined : _Object$assign$recove, _Object$assign$maxAge = _Object$assign.maxAge, maxAge = _Object$assign$maxAge === void 0 ? undefined : _Object$assign$maxAge, _Object$assign$acrVal = _Object$assign.acrValues, acrValues = _Object$assign$acrVal === void 0 ? undefined : _Object$assign$acrVal;
          meta = Object.assign(Object.assign({}, pkceMeta), {
            flow: flow,
            withCredentials: withCredentials,
            activationToken: activationToken,
            recoveryToken: recoveryToken,
            maxAge: maxAge,
            acrValues: acrValues
          });
          return _context.abrupt("return", meta);
        case 8:
        case "end":
          return _context.stop();
      }
    }, _callee);
  }));
  return _createTransactionMeta.apply(this, arguments);
}
function hasSavedInteractionHandle(authClient, options) {
  var savedMeta = getSavedTransactionMeta(authClient, options);
  if (savedMeta === null || savedMeta === void 0 ? void 0 : savedMeta.interactionHandle) {
    return true;
  }
  return false;
}
function getSavedTransactionMeta(authClient, options) {
  options = removeNils(options);
  options = Object.assign(Object.assign({}, authClient.options), options);
  var savedMeta;
  try {
    savedMeta = authClient.transactionManager.load(options);
  } catch (e) {}
  if (!savedMeta) {
    return;
  }
  if (isTransactionMetaValid(savedMeta, options)) {
    return savedMeta;
  }
  warn('Saved transaction meta does not match the current configuration. ' + 'This may indicate that two apps are sharing a storage key.');
}
function getTransactionMeta(_x2, _x3) {
  return _getTransactionMeta.apply(this, arguments);
}
function _getTransactionMeta() {
  _getTransactionMeta = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee2(authClient, options) {
    var validExistingMeta;
    return _regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) switch (_context2.prev = _context2.next) {
        case 0:
          options = removeNils(options);
          options = Object.assign(Object.assign({}, authClient.options), options);
          validExistingMeta = getSavedTransactionMeta(authClient, options);
          if (!validExistingMeta) {
            _context2.next = 5;
            break;
          }
          return _context2.abrupt("return", validExistingMeta);
        case 5:
          return _context2.abrupt("return", createTransactionMeta(authClient, options));
        case 6:
        case "end":
          return _context2.stop();
      }
    }, _callee2);
  }));
  return _getTransactionMeta.apply(this, arguments);
}
function saveTransactionMeta(authClient, meta) {
  authClient.transactionManager.save(meta, {
    muteWarning: true
  });
}
function clearTransactionMeta(authClient) {
  authClient.transactionManager.clear();
}
function isTransactionMetaValid(meta) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var keys = ['issuer', 'clientId', 'redirectUri', 'state', 'codeChallenge', 'codeChallengeMethod', 'activationToken', 'recoveryToken'];
  if (isTransactionMetaValidForOptions(meta, options, keys) === false) {
    return false;
  }
  var flow = options.flow;
  if (isTransactionMetaValidForFlow(meta, flow) === false) {
    return false;
  }
  return true;
}
function isTransactionMetaValidForFlow(meta, flow) {
  var shouldValidateFlow = flow && flow !== 'default' && flow !== 'proceed';
  if (shouldValidateFlow) {
    if (flow !== meta.flow) {
      return false;
    }
  }
  return true;
}
function isTransactionMetaValidForOptions(meta, options, keys) {
  var mismatch = keys.some(function (key) {
    var value = options[key];
    if (value && value !== meta[key]) {
      return true;
    }
  });
  return !mismatch;
}
export { clearTransactionMeta, createTransactionMeta, getSavedTransactionMeta, getTransactionMeta, hasSavedInteractionHandle, isTransactionMetaValid, isTransactionMetaValidForFlow, isTransactionMetaValidForOptions, saveTransactionMeta };